<template>
  <v-card class="my-2">
    <v-card-title>
      {{ selectedItem.name }}
    </v-card-title>
    
    <v-card-text>
      <!-- Toolbar Controls -->
      <div class="d-flex flex-wrap align-center mb-4 gap-2">
         <!-- Toggles -->
        <v-btn-toggle multiple dense class="mr-4">
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showAOIs = !showAOIs" icon :color="showAOIs ? 'primary' : ''" :loading="aoiLoading"><v-icon>{{icons.mdiDrawingBox}}</v-icon></v-btn>
                </template>
                <span>Toggle AOIs</span>
            </v-tooltip>
             <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showHeatmap = !showHeatmap" icon :color="showHeatmap ? 'primary' : ''" :loading="heatmapLoading"><v-icon>{{icons.mdiFire}}</v-icon></v-btn>
                </template>
                <span>Toggle Heatmap</span>
            </v-tooltip>
             <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showScanpath = !showScanpath" icon :color="showScanpath ? 'primary' : ''" :loading="scanpathLoading"><v-icon>{{icons.mdiMapMarkerPath}}</v-icon></v-btn>
                </template>
                <span>Toggle Scanpath</span>
            </v-tooltip>
             <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="isGrayscale = !isGrayscale" icon :color="isGrayscale ? 'grey darken-3' : ''"><v-icon>{{icons.mdiInvertColors}}</v-icon></v-btn>
                </template>
                <span>Toggle B&W</span>
            </v-tooltip>
             <v-tooltip bottom v-if="zones.length">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showKPIs = !showKPIs" :color="showKPIs ? 'secondary' : ''" :loading="analysisLoading"><v-icon>{{icons.mdiChartBar}}</v-icon></v-btn>
                </template>
                <span>KPI Overlay</span>
            </v-tooltip>
            <v-tooltip bottom v-if="zones.length">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showKPITable=true" :loading="analysisLoading"><v-icon>{{icons.mdiTableLarge}}</v-icon></v-btn>
                </template>
                <span>View Data Table</span>
            </v-tooltip>
        </v-btn-toggle>
        

        <!-- KPI Metric Selector -->
        <v-select 
            v-if="showKPIs" 
            v-model="selectedKpiMetric" 
            :items="kpiMetricsOptions" 
            label="Metrics" 
            multiple 
            small-chips
            deletable-chips
            dense 
            outlined 
            hide-details 
            class="mr-4" 
            style="max-width: 300px;">
        </v-select>

        <!-- Drawing Modes -->
        <v-btn-toggle v-model="currentMode" outlined class="mr-4">
             <v-tooltip v-for="option in modeOptions" :key="option.value" bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" :value="option.value" icon @click="currentMode = option.value"><v-icon>{{ option.icon }}</v-icon></v-btn>
                </template>
                <span>{{ option.label }}</span>
            </v-tooltip>
        </v-btn-toggle>

        <!-- AOI Edit Actions -->
        <template v-if="selectedZoneIndices.length > 0">
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon @click="copySelectedAOIs" v-bind="attrs" v-on="on"><v-icon>{{icons.mdiContentCopy}}</v-icon></v-btn>
                </template>
                <span>Copy Selected (Ctrl+C)</span>
            </v-tooltip>
            <v-divider vertical class="mx-2 my-2"></v-divider>
        </template>
        
        <v-tooltip bottom v-if="clipboardAOIs.length > 0">
            <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" @click="pasteAOIs" v-bind="attrs" v-on="on"><v-icon>{{icons.mdiContentPaste}}</v-icon></v-btn>
            </template>
            <span>Paste Copied AOIs (Ctrl+V)</span>
        </v-tooltip>

        <!-- Actions -->
        <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-btn color="error" class="square-btn mx-2" @click="showImport = true" v-bind="attrs" v-on="on"><v-icon>{{ icons.mdiFileDownloadOutline }}</v-icon></v-btn>
            </template>
            <span>Import</span>
        </v-tooltip>

        <v-spacer></v-spacer>
        
        <!-- Drawing Actions -->
        <v-btn color="warning" class="mr-2 square-btn" @click="undoDraw" v-if="currentMode === 'polygon'" :disabled="currentMode !== 'polygon' || points.length === 0"><v-icon>{{icons.mdiUndo}}</v-icon></v-btn>
        
         <!-- Export Menu -->
        <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
            <v-btn color="success" v-bind="attrs" v-on="on" class="square-btn mr-2"><v-icon>{{ icons.mdiDownload }}</v-icon></v-btn>
            </template>
            <v-list>
            <v-list-item @click="exportCanvas('heatmap')"><v-list-item-title>Image + Heatmap</v-list-item-title></v-list-item>
            <v-list-item @click="exportCanvas('aoi')"><v-list-item-title>Image + AOIs</v-list-item-title></v-list-item>
            <v-list-item @click="exportCanvas('scanpath')"><v-list-item-title>Image + Scanpath</v-list-item-title></v-list-item>
            </v-list>
        </v-menu>
      </div>

      <!-- Sliders -->
      <v-row class="mb-2">
         <v-col cols="12" md="3"><div class="d-flex align-center"><v-slider v-model="zoomLevel" label="Zoom" min="10" max="300" thumb-label hide-details></v-slider></div></v-col>
         <v-col cols="12" md="3"><div class="d-flex align-center"><v-slider v-model="heatmapOpacity" @change="opacityChange" label="Opacity" min="0.1" max="1" step="0.1" thumb-label hide-details></v-slider></div></v-col>
         <v-col cols="12" md="3"><div class="d-flex align-center"><v-slider v-model="heatmapRadius" @change="radiusChange" label="Radius" min="1" max="100" thumb-label hide-details></v-slider></div></v-col>
      </v-row>
    </v-card-text>


    <!-- Scanpath Controls -->
    <v-card-actions class="flex-column align-stretch pa-4" v-if="showScanpath">
        
        <v-slider
            v-model="currentIndex"
            :max="totalRespondents - 1"
            min="0"
            step="1"
            thumb-label
            color="primary"
            track-color="grey-lighten-2"
            hide-details
            class="w-100 mb-2"
        >
            <template v-slot:prepend>
                <span class="text-caption text-grey">1</span>
            </template>
            <template v-slot:append>
                <span class="text-caption text-grey">{{ totalRespondents }}</span>
            </template>
        </v-slider>

        <div class="d-flex justify-space-between w-100">
            <v-btn 
                variant="tonal" 
                color="primary" 
                @click="prevRespondent" 
                :disabled="currentIndex === 0"
                prepend-icon="mdi-arrow-left"
            >
                Previous
            </v-btn>

            <div class="text-body-2 align-self-center text-grey-darken-1">
                Respondent: <strong>{{ currentRespondentName }}</strong> {{ currentIndex + 1 }} / {{ totalRespondents }}
            </div>

            <v-btn 
                variant="tonal" 
                color="primary" 
                @click="nextRespondent" 
                :disabled="currentIndex === totalRespondents - 1"
                append-icon="mdi-arrow-right"
            >
                Next
            </v-btn>
        </div>
    </v-card-actions>
    <v-card-text class="image-workspace-area fill-height" style="position: relative; overflow: hidden;">
    
        <v-navigation-drawer
        v-model="aoiDrawer"
        absolute
        width="300"
        class="elevation-10"
        style="z-index: 10;"
        >
            <v-list dense class="aoi-list-content">
                <v-subheader>
                    Defined AOIs
                    <v-spacer></v-spacer>
                    <!-- Close button for the drawer -->
                     <v-btn icon color="info" class="mr-2 square-btn" @click="groupZones" :disabled="selectedZoneIndices.length < 2"><v-icon>{{icons.mdiGroup}}</v-icon></v-btn>
        
                    <v-btn icon small @click="aoiDrawer = false"><v-icon>{{icons.mdiClose}}</v-icon></v-btn>
                </v-subheader>
                
                <v-list-item
                    v-for="(zone, index) in zones"
                    :key="index"
                    :class="{ 'selected': selectedZoneIndices.includes(zone.id) }"
                    @click.exact="selectZone(zone.id)"
                    @click.shift.exact="selectZone(zone.id, true)"
                    @click.ctrl.exact="selectZone(zone.id, true)"
                    @click.meta.exact="selectZone(zone.id, true)"
                    :style="{ color: zone.color }"
                >
                    <v-list-item-content><v-list-item-title>{{ zone.name }}</v-list-item-title></v-list-item-content>
                    <v-list-item-action class="d-flex flex-row">
                        <v-btn icon x-small @click.stop="openEditLabelDialog(index)"><v-icon>{{icons.mdiPencil}}</v-icon></v-btn>
                        <v-btn icon x-small @click.stop="openDeleteConfirmDialog(index)"><v-icon color="error">{{icons.mdiDelete}}</v-icon></v-btn>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
            <v-list dense class="aoi-list-content" v-if="groups.length > 0">
                <v-subheader>Groups/Nets</v-subheader>
                <v-list-item
                    v-for="(zone, index) in groups"
                    :key="index"
                    @click="selectGroup(index)"
                    :class="{ 'selected': selectedGroupIndex == index }"
                >
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ zone.name }}
                        </v-list-item-title>
                        <template v-if="zone.aois.length > 0">
                            <v-list-item-subtitle v-for="aoi, aoiIdx in zone.aois">
                                {{(aoiIdx+1)+". "+zones.find(i => i.id == aoi).name }}
                            </v-list-item-subtitle>
                        </template>
                    </v-list-item-content>
                    <v-list-item-action class="d-flex flex-row">
                        <v-btn icon x-small @click.stop="openEditLabelDialog(index, group=true)">
                            <v-icon>{{icons.mdiPencil}}</v-icon>
                        </v-btn>
                        <v-btn icon x-small @click.stop="openDeleteNetConfirmDialog(index)">
                            <v-icon color="error">{{icons.mdiDelete}}</v-icon>
                        </v-btn>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
        </v-navigation-drawer>

        <v-btn
        v-if="!aoiDrawer"
        absolute
        top
        left
        small
        fab
        color="white"
        class="mt-2 ml-2"
        style="z-index: 5"
        @click="aoiDrawer = true"
        >
            <v-icon>{{icons.mdiFormatListBulleted}}</v-icon>
        </v-btn>

        <!-- 4. Viewport -->
        <div class="image-canvas-viewport-col fill-height"
            :style="{ 
                paddingLeft: aoiDrawer ? '300px' : '0px',
                transition: 'padding-left 0.2s cubic-bezier(0.25, 0.8, 0.5, 1)' 
            }"
        >
        <div class="image-canvas-scroll-container"  ref="scrollContainer">
            <div class="image-container stack-context" :style="zoomContainerStyle" ref="zoomContainer">
                <!-- Layers -->
                <v-img 
                    :src="selectedItem.media" 
                    ref="eftImage" 
                    @load="onImageLoad"
                    class="layer-base"
                    :class="{ 'grayscale-effect': isGrayscale }"
                    crossorigin="anonymous"
                />
                
                <div id="heatmap-container" ref="heatmapContainer" class="layer-overlay" :style="{ visibility: showHeatmap ? 'visible' : 'hidden' }"></div>
                <canvas 
                    id="drawing-canvas" 
                    ref="drawingCanvas" 
                    :class="currentMode"
                    @mousedown="handleMouseDown"
                    @mousemove="handleMouseMove"
                    @mouseup="handleMouseUp"
                    :style="{ visibility: showAOIs ? 'visible' : 'hidden' }"
                ></canvas>

                <!-- Scanpath Overlay Container -->
                <svg 
                    id="scanpath-container" 
                    ref="scanpathContainer"
                    class="scanpath-svg" 
                    :viewBox="`0 0 ${selectedItem.width} ${selectedItem.height}`"
                    preserveAspectRatio="none"
                    v-if="showScanpath"
                >
                    <!-- Saccades (Lines) -->
                    <polyline 
                        :points="saccadePoints" 
                        fill="none" 
                        stroke="#1976D2" 
                        stroke-width="3" 
                        stroke-dasharray="8,4"
                        stroke-opacity="0.6"
                        stroke-linejoin="round"
                    />

                    <!-- Fixations (Circles) -->
                    <g v-for="(fix, idx) in currentFixations" :key="fix.index">
                        <circle 
                            :cx="fix.fix_x" 
                            :cy="fix.fix_y" 
                            :r="calculateRadius(fix.duration)" 
                            fill="rgba(33, 150, 243, 0.4)" 
                            stroke="#1976D2" 
                            stroke-width="2"
                            class="fixation-circle"
                        >
                            <title>Fixation {{ idx + 1 }}: {{ fix.duration }}ms</title>
                        </circle>
                        <!-- Sequence Number -->
                        <text 
                            :x="fix.fix_x" 
                            :y="fix.fix_y" 
                            dy=".35em" 
                            text-anchor="middle" 
                            class="fixation-text"
                        >
                            {{ idx + 1 }}
                        </text>
                    </g>
                    <!-- Start Arrow -->
                    <path
                        v-if="startArrowPath"
                        :d="startArrowPath"
                        fill="none"
                        stroke="#00E676" 
                        stroke-width="4"
                        stroke-linejoin="round"
                        stroke-linecap="round"
                        class="start-arrow"
                    />
                </svg>
                <div 
                    v-if="analysisLoading"
                    class="layer-overlay d-flex align-center justify-center"
                    style="z-index: 5; background-color: rgba(0, 0, 0, 0.4);"
                >
                    <v-progress-circular indeterminate color="white" size="64" width="6"></v-progress-circular>
                </div>
            </div>
        </div>
        </div>
    </v-card-text>

    <!-- Dialogs -->
    <v-dialog v-model="showEditLabelDialog" max-width="400px">
        <v-card>
            <v-card-title>Edit AOI</v-card-title>
            <v-card-text>
                <v-text-field v-model="editedZoneLabel" outlined dense autofocus hide-details @keydown.enter="saveEditedAOI" class="mb-2"></v-text-field>
                <v-select v-model="editedElement" :items="groupedAOIElements" item-text="name" item-value="id" label="Element" outlined dense hide-details :loading="elementsLoading"></v-select>
            </v-card-text>
            <v-card-actions>
                <v-btn text @click="cancelEditAOI">Cancel</v-btn>
                <v-btn color="primary" @click="saveEditedAOI" :loading="aoiLoading">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteConfirmDialog" max-width="400px">
        <v-card>
            <v-card-title>Delete AOI</v-card-title>
            <v-card-text>Are you sure you want to delete this AOI? This action cannot be undone.</v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer> 
                <v-btn text @click="cancelDeleteAoi">Cancel</v-btn>
                <v-btn color="error" @click="deleteZone" :loading="aoiLoading">Delete</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <v-dialog v-model="showDeleteNetConfirmDialog" max-width="400px">
        <v-card>
            <v-card-title>Delete Net</v-card-title>
            <v-card-text>Are you sure you want to delete this Net? This action cannot be undone.</v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer> 
                <v-btn text @click="cancelDeleteNet">Cancel</v-btn>
                <v-btn color="error" @click="deleteGroup" :loading="netLoading">Delete</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
     
     <v-snackbar v-model="isSnackbarVisible">{{ snackBarMessage }}</v-snackbar>
     
    <oculis-kpi-table-dialog
      v-model="showKPITable"
      :analysis-data="analysisData"
    />
    
      <oculis-import-dialog
        v-model="showImport"
        :current-image="selectedItem"
        @aois-imported="aoisImport"
      />
  </v-card>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from '@vue/composition-api'
import OculisKpiTableDialog from '../oculis-kpi/OculisKpiTableDialog.vue'
import OculisImportDialog from '../oculis-dialogs/OculisImportDialog.vue'
import h337 from 'heatmapjs/heatmap.js'
import useOculis from '../../useOculis'

// Icons import
import {
    mdiVectorRectangle, mdiVectorPolygon, mdiVectorCircle, mdiCursorMove, mdiPencil,
    mdiPlus, mdiContentSaveOutline, mdiUndo, mdiGroup, mdiDelete, mdiFileDownloadOutline,
    mdiFire, mdiDrawingBox, mdiInvertColors, mdiChartBar, mdiTableLarge, mdiDownload,
    mdiClose, mdiFormatListBulleted, mdiMapMarkerPath, mdiContentCopy, mdiContentPaste
} from '@mdi/js'

export default {
  components: {
    OculisKpiTableDialog,
    OculisImportDialog
  },
  props: {
    selectedItem: { type: Object, required: true },
    medias: { type: Array, default: () => [] }
  },
  setup(props) {
    const { 
        heatmapData, heatmapLoading, fetchMediaHeatmap, 
        analysisData, analysisLoading, fetchMediaAnalysis,
        aoiLoading, bulkCreateAOIs, addAOI, updateAOI, deleteAOI,
        netLoading, addNet, updateNet, deleteNet,
        mediaData, mediasLoading, fetchMedia, generateGroupId,
        fetchScanpath, scanpathData, scanpathLoading,
        elementsLoading, elementsOptions, fetchAOIElements,
    } = useOculis()

    // --- State ---
    const scrollContainer = ref(null)
    const drawingCanvas = ref(null)
    const aoiDrawer = ref(true)
    const eftImage = ref(null)
    const heatmapContainer = ref(null)
    const scanpathContainer = ref(null)
    let ctx = null
    let heatmapInstance = null

    // UI Toggles
    const showHeatmap = ref(false)
    const showAOIs = ref(true)
    const showKPITable = ref(false)
    const showScanpath = ref(false)
    const showImport = ref(false)
    const isGrayscale = ref(false)
    const showKPIs = ref(false)
    const zoomLevel = ref(100)
    
    // Data
    const zones = ref([])
    const groups = ref([])
    const points = ref([])
    const selectedZoneIndices = ref([])
    const selectedZoneIndex = ref(null)
    const selectedGroupIndex = ref(null)
    const clipboardAOIs = ref([]) // For Copy & Paste
    
    // Drawing
    const currentMode = ref('edit-points')
    const drawing = ref(false)
    const currentShape = ref(null)
    const dragStart = ref(null)
    
    // Extracted dragging state logic
    const draggingZoneId = ref(null)
    const draggingPointIndex = ref(null)
    const originalDragState = ref(null)

    const modeOptions = [
        {label: 'Select/Edit', value:'edit-points', icon:mdiPencil},
        {label: 'Move', value:'move', icon:mdiCursorMove},
        {label: 'Rectangle', value:'rectangle', icon:mdiVectorRectangle},
        {label: 'Circle', value:'circle', icon:mdiVectorCircle},
        {label: 'Polygon', value:'polygon', icon:mdiVectorPolygon},
    ]
    const zoneColor = ref('#ff0000')

    // --- Color Palette ---
    const colorPalette = [
        '#FF6347', '#4682B4', '#32CD32', '#FFD700', '#8A2BE2', 
        '#FF69B4', '#00CED1', '#FF4500', '#ADFF2F', '#BA55D3'
    ];
    const nextColorIndex = ref(0);

    const getNextColor = () => {
        const color = colorPalette[nextColorIndex.value];
        nextColorIndex.value = (nextColorIndex.value + 1) % colorPalette.length;
        return color;
    };

    // Heatmap config
    const heatmapOpacity = ref(0.6)
    const heatmapRadius = ref(25)
    const heatmapConfig = ref({
        minOpacity: 0.0001,
        maxOpacity: .78,
        blur: 1,
        gradient: {
            '0': 'blue', '.4': 'green', '.5': 'green', '.7': 'yellow', '.9': 'red'
        }
    })

    // Scaling
    const imageNaturalWidth = ref(0)
    const imageNaturalHeight = ref(0)
    const scaleX = ref(1) 
    const scaleY = ref(1)

    // KPI
    const selectedKpiMetric = ref(['order', 'noting', 'duration']);
    const kpiMetricsOptions = [
            {text:'Noting', value:'noting'}, 
            {text:'Visit Duration', value:'duration'},
            {text:'Order', value:'order'},
    ]

    // Dialogs / Misc
    const showEditLabelDialog = ref(false)
    const editedZoneLabel = ref('')
    const editedElement = ref('')
    const editingZoneIndex = ref(null)
    const showDeleteConfirmDialog = ref(false);
    const showDeleteNetConfirmDialog = ref(false)

    const isSnackbarVisible = ref(false)
    const snackBarMessage = ref('')

    // --- Computed ---
    const zoomContainerStyle = computed(() => ({
      transform: `scale(${zoomLevel.value / 100})`,
      transformOrigin: 'top left',
    }))

    // --- Core Methods ---
    
    const onImageLoad = async () => {
        if(!eftImage.value || !eftImage.value.image) return;
        imageNaturalWidth.value = eftImage.value.image.naturalWidth
        imageNaturalHeight.value = eftImage.value.image.naturalHeight
        
        if (scrollContainer.value) {
            const availableWidth = scrollContainer.value.clientWidth - 24
            const availableHeight = scrollContainer.value.clientHeight - 24
            const scaleW = availableWidth / imageNaturalWidth.value
            const scaleH = 1
            const scale = Math.min(scaleW, scaleH)
            
            if (scale < 1) {
                zoomLevel.value = Math.floor(scale * 100)
            } else {
                zoomLevel.value = 100
            }
        }
        
        const maxDim = Math.max(imageNaturalWidth.value, imageNaturalHeight.value);
        heatmapRadius.value = Math.max(20, Math.min(Math.round((maxDim / 1500) * 25), 100));

        if(drawingCanvas.value) {
            drawingCanvas.value.width = imageNaturalWidth.value
            drawingCanvas.value.height = imageNaturalHeight.value
            ctx = drawingCanvas.value.getContext('2d', { willReadFrequently: true }) 
        }
        if(heatmapContainer.value) {
            heatmapContainer.value.style.width = `${imageNaturalWidth.value}px`
            heatmapContainer.value.style.height = `${imageNaturalHeight.value}px`
        }

        initHeatmap()
        
        if(props.selectedItem.id) {
            await fetchMedia({id: props.selectedItem.id})
            await fetchMediaHeatmap(mediaData.value.project, mediaData.value.id)
            if(heatmapData.value && heatmapInstance) {
                const dataWithRadius = (heatmapData.value.data || []).map(p => ({...p}));
                heatmapInstance.setData({ ...heatmapData.value, data: dataWithRadius })
                updateHeatMinMax(dataWithRadius, imageNaturalWidth.value, imageNaturalHeight.value, heatmapInstance);
            }
            const aois = mediaData.value.aois.map(aoi => ({
                    ...aoi,
                    project: mediaData.value.project
                }))
            zones.value = aois ? JSON.parse(JSON.stringify(aois)) : []
            groups.value = mediaData.value.nets ? JSON.parse(JSON.stringify(mediaData.value.nets)) : []
            redrawAllZones()
            await fetchMediaAnalysis(mediaData.value.project, mediaData.value.id)
            await fetchScanpath(mediaData.value.project, mediaData.value.id)
        }
    }
    
    const initHeatmap = () => {
        const originalGetContext = HTMLCanvasElement.prototype.getContext;
        HTMLCanvasElement.prototype.getContext = function(type, attributes) {
            if (type === '2d') {
                attributes = { ...attributes, willReadFrequently: true };
            }
            return originalGetContext.call(this, type, attributes);
        };

        try {
            heatmapInstance = h337.create({
                container: heatmapContainer.value,
                radius: heatmapRadius.value,
                maxOpacity: heatmapOpacity.value,
                ...heatmapConfig.value
            });
        } finally {
            HTMLCanvasElement.prototype.getContext = originalGetContext;
        }
    }

    const updateHeatMinMax = (data, width, height, heatmap) => {
        var nSlice = 100;
        var xSlice = width/nSlice; 
        var ySlice = height/nSlice; 
        xSlice = xSlice<100?xSlice:100;
        ySlice = ySlice<100?ySlice:100;
        var aArr = [];
        
        for (var i=0, n=data.length; i < n; i++){
            var c = data[i];
            var temp = [];
            for(var w=-1; w<=1; w++){
                for(var h=-1; h<=1; h++){
                    if (w !== 0 && h !== 0) continue;
                    var r = c['radius'],
                        x = parseInt(c['x'])+(w*r),
                        y = parseInt(c['y'])+(h*r);
                    var binX = Math.round(x/xSlice-1) + 1,
                        binY = Math.round(y/ySlice-1) + 1;
                
                    var dist = Math.sqrt(Math.pow(xSlice*binX-c['x'],2) + Math.pow(ySlice*binY-c['y'],2));
                    var key = binX+'-'+binY;
                    var value = temp[key] || 0;
                    
                    if(dist<=r){
                        var cValue = (1-dist/r)*c['value'];
                        temp[key] = value>cValue?value:cValue;
                    }
                }
            }
            for (var key in temp){
                var value = aArr[key] || 0;
                aArr[key] = temp[key]+value;
            }
        }
        
        var vArr = [];
        for (var key in aArr) {
            if(aArr[key] !== 0) vArr.push(aArr[key]);
        }
        vArr.sort(function(a,b){return a-b;});
        var index = Math.floor(vArr.length * .02);
        heatmap.setDataMax(vArr[vArr.length - index]);
        heatmap.setDataMin(vArr[index]);
    }

    const convertToNatural = (renderedX, renderedY) => ({ x: renderedX * scaleX.value, y: renderedY * scaleY.value });
    const convertToRendered = (naturalX, naturalY) => ({ x: naturalX / scaleX.value, y: naturalY / scaleY.value });

    // 2. Drawing / Redrawing
    const redrawAllZones = () => {
        if(!ctx) return;
        ctx.clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height)
        
        zones.value.forEach((zone, idx) => {
             const isSel = selectedZoneIndices.value.includes(zone.id) || selectedZoneIndex.value === zone.id
             drawZoneShape(zone, isSel)
             if(showKPIs.value) drawKpiOverlay(zone)
        })
        
        if(currentMode.value === 'polygon' && points.value.length > 0) {
            ctx.beginPath();
            points.value.forEach((p, i) => { i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y) });
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 2;
            ctx.stroke();

            const startP = points.value[0];
            ctx.beginPath();
            ctx.arc(startP.x, startP.y, 5, 0, 2 * Math.PI);
            ctx.fillStyle = 'red';
            ctx.fill();
        }
    }

    const drawZoneShape = (zone, isSelected=false) => {
        ctx.fillStyle = zone.color;
        ctx.strokeStyle = isSelected ? 'blue' : zone.color;
        ctx.lineWidth = isSelected ? 4 : 2;
        ctx.globalAlpha = isSelected ? 0.7 : zone.opacity || 0.5;

        ctx.beginPath();
        if(zone.shape === 'rectangle') {
            ctx.rect(zone.x, zone.y, zone.width, zone.height);
        } else if (zone.shape === 'circle') {
             ctx.arc(zone.x, zone.y, zone.radius, 0, 2 * Math.PI);
        } else if (zone.shape === 'polygon') {
            zone.points.forEach((p, i) => { i===0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y) });
            ctx.closePath();
        }
        ctx.fill();
        ctx.stroke();
        ctx.globalAlpha = 1;

        if (isSelected && currentMode.value === 'edit-points') {
            if(zone.shape === 'rectangle') {
                drawPoint(zone.x, zone.y);
                drawPoint(zone.x + zone.width, zone.y);
                drawPoint(zone.x, zone.y + zone.height);
                drawPoint(zone.x + zone.width, zone.y + zone.height);
            } else if (zone.shape === 'circle') {
                drawPoint(zone.x + zone.radius, zone.y);
                drawPoint(zone.x - zone.radius, zone.y);
                drawPoint(zone.x, zone.y + zone.radius);
                drawPoint(zone.x, zone.y - zone.radius);
            } else if (zone.shape === 'polygon') {
                zone.points.forEach((p) => drawPoint(p.x, p.y));
            }
        }
    }

    const drawPoint = (x, y) => {
        if (!ctx) return;
        ctx.fillStyle = 'blue';
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fill();
    }

    // 3. Mouse Handlers
    const handleMouseDown = (e) => {
        const canvas = drawingCanvas.value;
        const ctxVal = ctx; 
        const mode = currentMode.value;
        const zoom = zoomLevel.value;

        if (!canvas || !ctxVal) return;

        const rect = canvas.getBoundingClientRect();
        const xRendered = e.clientX - rect.left;
        const yRendered = e.clientY - rect.top;

        const zoomFactor = zoom / 100;
        const xCanvas = xRendered / zoomFactor;
        const yCanvas = yRendered / zoomFactor;
        const { x: xNatural, y: yNatural } = convertToNatural(xCanvas, yCanvas);

        const HIT_THRESHOLD_SQ = 25; 
        const isHit = (x1, y1, x2, y2) => ((x1 - x2) ** 2 + (y1 - y2) ** 2) <= HIT_THRESHOLD_SQ;

        // Point Drag Detection (Multi-Select Supported)
        if (mode === 'edit-points' && selectedZoneIndices.value.length > 0) {
            let foundZoneId = null;
            let foundPointIndex = -1;

            // Iterate backward to respect rendering order
            for (let i = selectedZoneIndices.value.length - 1; i >= 0; i--) {
                const id = selectedZoneIndices.value[i];
                const zone = zones.value.find(item => item.id === id);
                if (!zone) continue;

                if (zone.shape === 'polygon') {
                    for (let j = 0; j < zone.points.length; j++) {
                        const pRendered = convertToRendered(zone.points[j].x, zone.points[j].y);
                        if (isHit(xCanvas, yCanvas, pRendered.x, pRendered.y)) { foundPointIndex = j; break; }
                    }
                } 
                else if (zone.shape === 'rectangle') {
                    const coords = [
                        { x: zone.x, y: zone.y }, { x: zone.x + zone.width, y: zone.y },
                        { x: zone.x, y: zone.y + zone.height }, { x: zone.x + zone.width, y: zone.y + zone.height }
                    ];
                    for (let j = 0; j < 4; j++) {
                        const pRendered = convertToRendered(coords[j].x, coords[j].y);
                        if (isHit(xCanvas, yCanvas, pRendered.x, pRendered.y)) { foundPointIndex = j; break; }
                    }
                } 
                else if (zone.shape === 'circle') {
                    const coords = [
                        { x: zone.x + zone.radius, y: zone.y }, { x: zone.x - zone.radius, y: zone.y },
                        { x: zone.x, y: zone.y + zone.radius }, { x: zone.x, y: zone.y - zone.radius }
                    ];
                    for (let j = 0; j < 4; j++) {
                        const pRendered = convertToRendered(coords[j].x, coords[j].y);
                        if (isHit(xCanvas, yCanvas, pRendered.x, pRendered.y)) { foundPointIndex = j; break; }
                    }
                }

                if (foundPointIndex !== -1) {
                    foundZoneId = zone.id;
                    break;
                }
            }

            if (foundZoneId !== null) {
                draggingZoneId.value = foundZoneId;
                draggingPointIndex.value = foundPointIndex;
                dragStart.value = { x: xCanvas, y: yCanvas };
                
                // Save original state for precise multi-scaling math
                if (selectedZoneIndices.value.length > 1) {
                    originalDragState.value = JSON.parse(JSON.stringify(zones.value.filter(z => selectedZoneIndices.value.includes(z.id))));
                }
                return; 
            }
        }

        // Automatic Selection Logic (Supports Multi-Select with Shift/Ctrl)
        if (mode === 'move' || mode === 'edit-points') {
            const foundIndex = findZoneAtCoordinates(xNatural, yNatural);
            
            if (foundIndex !== null) {
                if (e.shiftKey || e.ctrlKey || e.metaKey) {
                    const idx = selectedZoneIndices.value.indexOf(foundIndex);
                    if (idx === -1) {
                        selectedZoneIndices.value.push(foundIndex);
                    } else {
                        selectedZoneIndices.value.splice(idx, 1);
                    }
                    selectedZoneIndex.value = selectedZoneIndices.value.length > 0 ? selectedZoneIndices.value[selectedZoneIndices.value.length - 1] : null;
                } else {
                    if (!selectedZoneIndices.value.includes(foundIndex)) {
                        selectedZoneIndex.value = foundIndex;
                        selectedZoneIndices.value = [foundIndex];
                    }
                }
            } else {
                if (!(e.shiftKey || e.ctrlKey || e.metaKey)) {
                    selectedZoneIndex.value = null;
                    selectedZoneIndices.value = [];
                }
            }
            redrawAllZones(); 
        }

        if (mode === 'move' && selectedZoneIndices.value.length > 0) {
            dragStart.value = { x: xCanvas, y: yCanvas };
            return;
        }

        // Drawing Setup...
        drawing.value = true;
        const opacity = 0.5;
        switch (mode) {
            case 'rectangle':
                zoneColor.value = getNextColor(); 
                currentShape.value = { shape: 'rectangle', x: xNatural, y: yNatural, width: 0, height: 0, color: zoneColor.value, opacity };
                break;
            case 'circle':
                zoneColor.value = getNextColor();
                currentShape.value = { shape: 'circle', x: xNatural, y: yNatural, radius: 0, color: zoneColor.value, opacity };
                break;
            case 'polygon':
                if (points.value.length >= 3) {
                    const startPoint = points.value[0];
                    const startRendered = convertToRendered(startPoint.x, startPoint.y);
                    const distSq = (xCanvas - startRendered.x) ** 2 + (yCanvas - startRendered.y) ** 2;
                    if (isHit(xCanvas, yCanvas, startRendered.x, startRendered.y) || distSq <= 100) {
                        addZone(); 
                        drawing.value = false;
                        break;
                    }
                }
                if (points.value.length === 0) zoneColor.value = getNextColor();
                points.value.push({ x: xNatural, y: yNatural });
                currentShape.value = { shape: 'polygon', points: points.value, color: zoneColor.value, opacity };
                redrawAllZones(); 
                break;
        }
    };

    const handleMouseMove = (e) => {
        const canvas = drawingCanvas.value;
        const ctxVal = ctx; 
        const mode = currentMode.value;
        const zoom = zoomLevel.value;
        
        if (!canvas || !ctxVal) return;

        const rect = canvas.getBoundingClientRect();
        const xRendered = e.clientX - rect.left;
        const yRendered = e.clientY - rect.top;
        const zoomFactor = zoom / 100;
        const xCanvas = xRendered / zoomFactor;
        const yCanvas = yRendered / zoomFactor;
        const { x: xNatural, y: yNatural } = convertToNatural(xCanvas, yCanvas);

        // --- MODE: MOVE (Multi-Support) ---
        if (mode === 'move' && selectedZoneIndices.value.length > 0 && dragStart.value) {
            const start = dragStart.value;
            const dxRendered = xCanvas - start.x;
            const dyRendered = yCanvas - start.y;
            const dxNatural = dxRendered * scaleX.value;
            const dyNatural = dyRendered * scaleY.value;

            selectedZoneIndices.value.forEach(id => {
                const zone = zones.value.find(item => item.id === id);
                if (zone) {
                    if (zone.shape === 'polygon') {
                        const pts = zone.points;
                        for (let i = 0; i < pts.length; i++) {
                            pts[i].x += dxNatural;
                            pts[i].y += dyNatural;
                        }
                    } else {
                        zone.x += dxNatural;
                        zone.y += dyNatural;
                    }
                }
            });

            dragStart.value = { x: xCanvas, y: yCanvas };
            redrawAllZones();
            return;
        }

        // --- MODE: EDIT POINTS (Single Reshape & Multi-Scale) ---
        if (mode === 'edit-points' && draggingZoneId.value !== null && draggingPointIndex.value !== null && dragStart.value !== null) {
            
            // Single Item - Independent shape adjustment
            if (selectedZoneIndices.value.length === 1) {
                const zone = zones.value.find(item => item.id == draggingZoneId.value);
                const pointIdx = draggingPointIndex.value;

                if (zone) {
                    if (zone.shape === 'polygon') {
                        zone.points[pointIdx].x = xNatural;
                        zone.points[pointIdx].y = yNatural;
                    } 
                    else if (zone.shape === 'rectangle') {
                        const ox = zone.x, oy = zone.y, ow = zone.width, oh = zone.height;
                        switch (pointIdx) {
                            case 0: zone.x = xNatural; zone.y = yNatural; zone.width = (ox + ow) - xNatural; zone.height = (oy + oh) - yNatural; break;
                            case 1: zone.y = yNatural; zone.width = xNatural - ox; zone.height = (oy + oh) - yNatural; break;
                            case 2: zone.x = xNatural; zone.width = (ox + ow) - xNatural; zone.height = yNatural - oy; break;
                            case 3: zone.width = xNatural - ox; zone.height = yNatural - oy; break;
                        }
                        if (zone.width < 0) { zone.x += zone.width; zone.width = -zone.width; }
                        if (zone.height < 0) { zone.y += zone.height; zone.height = -zone.height; }
                    } 
                    else if (zone.shape === 'circle') {
                        if (pointIdx === 0) zone.radius = Math.abs(xNatural - zone.x);
                        else if (pointIdx === 1) zone.radius = Math.abs(zone.x - xNatural);
                        else if (pointIdx === 2) zone.radius = Math.abs(yNatural - zone.y);
                        else if (pointIdx === 3) zone.radius = Math.abs(zone.y - yNatural);
                    }
                }
            } 
            // Multi Items - Uniform Relative Scaling
            else if (originalDragState.value) {
                const activeOrigZone = originalDragState.value.find(z => z.id === draggingZoneId.value);
                if (activeOrigZone) {
                    const activeOrigC = getShapeCentroid(activeOrigZone);
                    const activeOrigP = getPointByIndex(activeOrigZone, draggingPointIndex.value);
                    const distOrig = Math.hypot(activeOrigP.x - activeOrigC.x, activeOrigP.y - activeOrigC.y) || 1;
                    const distNew = Math.hypot(xNatural - activeOrigC.x, yNatural - activeOrigC.y);
                    const scale = distNew / distOrig;

                    selectedZoneIndices.value.forEach(id => {
                        const orig = originalDragState.value.find(z => z.id === id);
                        const current = zones.value.find(z => z.id === id);
                        if (!orig || !current) return;
                        
                        const origC = getShapeCentroid(orig);
                        if (current.shape === 'rectangle') {
                            current.width = orig.width * scale;
                            current.height = orig.height * scale;
                            current.x = origC.x - (current.width / 2);
                            current.y = origC.y - (current.height / 2);
                        } else if (current.shape === 'circle') {
                            current.radius = orig.radius * scale;
                        } else if (current.shape === 'polygon') {
                            current.points = orig.points.map(p => ({
                                x: origC.x + (p.x - origC.x) * scale,
                                y: origC.y + (p.y - origC.y) * scale
                            }));
                        }
                    });
                }
            }
            
            dragStart.value = { x: xCanvas, y: yCanvas };
            redrawAllZones();
            return; 
        }

        const isActivePolygon = currentMode.value === 'polygon' && points.value.length > 0;
        if ((!drawing.value && !isActivePolygon) || !currentShape.value) return;

        ctxVal.clearRect(0, 0, canvas.width, canvas.height);
        redrawAllZones(); 

        const shape = currentShape.value;
        if (!shape) return;

        if (shape.shape === 'rectangle') {
            shape.width = xNatural - shape.x; shape.height = yNatural - shape.y; drawZoneShape(shape)
        } else if (shape.shape === 'circle') {
            const dx = xNatural - shape.x, dy = yNatural - shape.y; shape.radius = Math.sqrt(dx * dx + dy * dy); drawZoneShape(shape)
        } else if (shape.shape === 'polygon') {
            drawZoneShape(shape)
            if (points.value.length > 0) {
                const lastPoint = points.value[points.value.length - 1];
                ctx.beginPath(); ctx.moveTo(lastPoint.x, lastPoint.y); ctx.lineTo(xNatural, yNatural);
                ctx.strokeStyle = shape.color; ctx.lineWidth = 2; ctx.setLineDash([6, 4]); ctx.stroke(); ctx.setLineDash([]); 
            }
        }
    }

    const handleMouseUp = async () => {
        const mode = currentMode.value;
        
        // Save Multi-Move
        if (mode === 'move' && selectedZoneIndices.value.length > 0 && dragStart.value) {
            
            // LOCK CANVAS IMMEDIATELY
            dragStart.value = null;

            const updatePromises = selectedZoneIndices.value.map(id => {
                const zone = zones.value.find(z => z.id === id);
                if (zone) {
                    const payload = transformToFlatCoordinates(zone);
                    payload.project = mediaData.value.project;
                    return updateAOI(payload);
                }
            });
            await Promise.all(updatePromises);
            if (mediaData.value) fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);
            return;
        }

        // Save Point Edits (Single or Multi-Scale)
        if (mode === 'edit-points' && draggingZoneId.value !== null && dragStart.value) {
            
            // Cache state before nullifying
            const isMulti = selectedZoneIndices.value.length > 1;
            const targetId = draggingZoneId.value;

            // LOCK CANVAS IMMEDIATELY
            draggingPointIndex.value = null;
            draggingZoneId.value = null;
            dragStart.value = null;
            originalDragState.value = null;

            if (isMulti) {
                const updatePromises = selectedZoneIndices.value.map(id => {
                    const zone = zones.value.find(z => z.id === id);
                    if (zone) {
                        const payload = transformToFlatCoordinates(zone);
                        payload.project = mediaData.value.project;
                        return updateAOI(payload);
                    }
                });
                await Promise.all(updatePromises);
            } else {
                const zone = zones.value.find(z => z.id === targetId);
                if (zone) {
                    const payload = transformToFlatCoordinates(zone);
                    payload.project = mediaData.value.project;
                    await updateAOI(payload);
                }
            }
            if (mediaData.value) fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);
            return;
        }
        
        drawing.value = false;
        const shape = currentShape.value;

        if (shape && (shape.shape === 'rectangle' || shape.shape === 'circle')) {
            if (shape.shape === 'rectangle') {
                if (shape.width < 0) { shape.x += shape.width; shape.width = -shape.width; }
                if (shape.height < 0) { shape.y += shape.height; shape.height = -shape.height; }
            }
            const selItem = mediaData.value; 
            shape.name = "";
            shape.project = selItem ? selItem.project : null;
            shape.media = selItem ? selItem.id : null;
            zones.value.push(shape);
            openEditLabelDialog(zones.value.length - 1);
            currentShape.value = null;
            ctx.clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height);
            redrawAllZones(); 
        }
    }

    const copySelectedAOIs = () => {
        if (selectedZoneIndices.value.length === 0) return;
        clipboardAOIs.value = selectedZoneIndices.value.map(id => {
            const zone = zones.value.find(z => z.id === id);
            return zone ? JSON.parse(JSON.stringify(zone)) : null;
        }).filter(Boolean);
        
        snackBarMessage.value = `${clipboardAOIs.value.length} AOI(s) copied to clipboard`;
        isSnackbarVisible.value = true;
    };

    const pasteAOIs = async () => {
        if (clipboardAOIs.value.length === 0) return;
        
        const newAOIsPayload = [];
        const offset = 20;
        
        clipboardAOIs.value.forEach(zone => {
            const newZone = JSON.parse(JSON.stringify(zone));
            delete newZone.id; // Treat as brand new
            newZone.name = (newZone.name || "AOI") + " copy";
            
            // Offset coordinates slightly so user sees duplicated overlay
            if (newZone.shape === 'rectangle' || newZone.shape === 'circle') {
                newZone.x += offset; newZone.y += offset;
            } else if (newZone.shape === 'polygon') {
                newZone.points.forEach(p => { p.x += offset; p.y += offset; });
            }
            
            const payload = transformToFlatCoordinates(newZone);
            payload.project = mediaData.value.project;
            payload.media = mediaData.value.id;
            newAOIsPayload.push(payload);
        });

        try {
            const payloadObj = {
                project: mediaData.value.project,
                media: mediaData.value.id,
                aois: newAOIsPayload,
                nets: []
            };
            
            const response = await bulkCreateAOIs(payloadObj);
            
            if (response && response.aois) {
                zones.value.push(...response.aois);
                selectedZoneIndices.value = response.aois.map(z => z.id);
                selectedZoneIndex.value = selectedZoneIndices.value[selectedZoneIndices.value.length - 1];
                
                redrawAllZones();
                snackBarMessage.value = `${response.aois.length} AOI(s) pasted and saved`;
                isSnackbarVisible.value = true;
                
                fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);
            }
        } catch (error) {
            console.error("Paste failed", error);
        }
    };

    const handleKeyDown = (e) => {
        if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
        
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
            copySelectedAOIs(); e.preventDefault();
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
            pasteAOIs(); e.preventDefault();
        }
    };

    // --- Geometric Point-in-Shape Checks ---
    const isPointInRectangle = (pointX, pointY, rect) => (pointX >= rect.x && pointX <= rect.x + rect.width && pointY >= rect.y && pointY <= rect.y + rect.height);
    const isPointInCircle = (pointX, pointY, circle) => (Math.sqrt((pointX - circle.x) ** 2 + (pointY - circle.y) ** 2) <= circle.radius);
    const isPointInPolygon = (pointX, pointY, polygonPoints) => {
        let intersect = 0;
        for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
            const xi = polygonPoints[i].x, yi = polygonPoints[i].y, xj = polygonPoints[j].x, yj = polygonPoints[j].y;
            if (((yi > pointY) !== (yj > pointY)) && (pointX < (xj - xi) * (pointY - yi) / (yj - yi) + xi)) intersect++;
        }
        return intersect % 2 === 1;
    };
    const findZoneAtCoordinates = (xNatural, yNatural) => {
        for (let i = zones.value.length - 1; i >= 0; i--) {
            const zone = zones.value[i];
            if (zone.shape === 'rectangle' && isPointInRectangle(xNatural, yNatural, zone)) return zone.id;
            else if (zone.shape === 'circle' && isPointInCircle(xNatural, yNatural, zone)) return zone.id;
            else if (zone.shape === 'polygon' && isPointInPolygon(xNatural, yNatural, zone.points)) return zone.id;
        }
        return null; 
    };

    // 4. Sidebar / Actions
    const selectZone = (index, multi = false) => {
        if (multi) {
            toggleItemInArray(selectedZoneIndices, index);
        } else {
            selectedZoneIndices.value = [index];
        }
        selectedZoneIndex.value = selectedZoneIndices.value.length > 0 ? selectedZoneIndices.value[selectedZoneIndices.value.length - 1] : null;
        selectedGroupIndex.value = null; 
        redrawAllZones();
    }
    const toggleItemInArray = (arrRef, item) => {
        const index = arrRef.value.indexOf(item);
        if (index === -1) arrRef.value.push(item); else arrRef.value.splice(index, 1);
    }

    const openEditLabelDialog = (index, group=false) => {
        if (group) {
            selectedGroupIndex.value = index;
            editedZoneLabel.value = groups.value[index].name || '';
        } else {
            selectedGroupIndex.value = null
            editedZoneLabel.value = zones.value[index].name || '';
            editedElement.value = zones.value[index].element || '';
        }
        editingZoneIndex.value = index;
        showEditLabelDialog.value = true;
    };

    const saveEditedAOI = async () => {
        if ( editingZoneIndex.value !== null && editedZoneLabel.value.trim() !== '') {
            let zone = {}
            if (selectedGroupIndex.value === null) {
                zones.value[editingZoneIndex.value].name = editedZoneLabel.value.trim();
                zone = zones.value[editingZoneIndex.value]
                zone.element = editedElement.value
                const payload = transformToFlatCoordinates(zone)
                if (zone.id === undefined || zone.id === null) {
                    zones.value[editingZoneIndex.value] = await addAOI(payload)
                    fetchMediaAnalysis(payload.project, payload.media)
                }
                else await updateAOI(payload)
            } else {
                groups.value[editingZoneIndex.value].name = editedZoneLabel.value.trim();
                zone = groups.value[editingZoneIndex.value]
                if (zone.id === undefined || zone.id === null) groups.value[editingZoneIndex.value] = await addNet(zone)
                else updateNet(zone)
            }
        }
        editingZoneIndex.value = null;
        showEditLabelDialog.value = false;
        editedZoneLabel.value = ''; editedElement.value = '';
    }
    
    const cancelEditAOI = () => {
        if (editingZoneIndex.value !== null && zones.value[editingZoneIndex.value]?.name.trim() === '') zones.value.splice(editingZoneIndex.value, 1)
        if (editingZoneIndex.value !== null && selectedGroupIndex.value !== null && groups.value[editingZoneIndex.value]?.name.trim() === '') groups.value.splice(editingZoneIndex.value, 1)
        redrawAllZones()
        editingZoneIndex.value = null; showEditLabelDialog.value = false; editedZoneLabel.value = ''; editedElement.value = '';
    }

    const openDeleteConfirmDialog = (index) => { editingZoneIndex.value = index; showDeleteConfirmDialog.value = true; };
    const cancelDeleteAoi = () => { showDeleteConfirmDialog.value = false; };
    const openDeleteNetConfirmDialog = (index) => { editingZoneIndex.value = index; showDeleteNetConfirmDialog.value = true; };
    const cancelDeleteNet = () => { showDeleteNetConfirmDialog.value = false; };

    const deleteZone = async () => {
        try {
            const zone = zones.value[editingZoneIndex.value]
            await deleteAOI(zone)
            zones.value.splice(editingZoneIndex.value, 1);
            editingZoneIndex.value = null
            selectedZoneIndex.value = null; 
            selectedZoneIndices.value = selectedZoneIndices.value.filter(i => i !== editingZoneIndex.value);
            fetchMediaAnalysis(mediaData.value.project, zone.media)
            redrawAllZones(); showDeleteConfirmDialog.value = false;
        } catch (error) { console.error("Failed to delete AOI", error); }
    }
    
    const deleteGroup = async () => {
        try {
            const targetIndex = editingZoneIndex.value; 
            const group = groups.value[targetIndex];
            await deleteNet(group);
            groups.value.splice(targetIndex, 1); 
            editingZoneIndex.value = null; selectedGroupIndex.value = null; 
            selectedZoneIndices.value = selectedZoneIndices.value.filter(i => i !== targetIndex); 
            fetchMediaAnalysis(mediaData.value.project, group.media);
            redrawAllZones(); showDeleteNetConfirmDialog.value = false;
        } catch (error) { console.error("Failed to delete Net", error); }
    }

    const transformToFlatCoordinates = (zone) => {
        const { x, y, width, height, radius, points, ...metaData } = zone;
        let coordinates = [];
        const toInt = (val) => Math.round(Number(val));

        if (zone.shape === 'polygon' && Array.isArray(points)) coordinates = points.flatMap(p => [toInt(p.x), toInt(p.y)]);
        else if (zone.shape === 'rectangle') coordinates = [x, y, x+width, y+height].map(toInt);
        else if (zone.shape === 'circle') coordinates = [x, y, radius].map(toInt);

        return { ...metaData, coordinates };
    };

    const drawKpiOverlay = (zone) => {
        if (!ctx || !analysisData.value[zone.id]) return;
        const dataObj = analysisData.value[zone.id]["time_slots"]["100"];
        if (!dataObj) return;

        const metrics = Array.isArray(selectedKpiMetric.value) ? selectedKpiMetric.value : [];
        if (metrics.length === 0) return;
        const lines = [];

        metrics.forEach(metric => {
            const value = dataObj[metric];
            if (value === undefined || value === null) return;
            let formattedValue = '';
            let label = metric.toUpperCase() === 'TTFF' ? 'TTFF' : metric.charAt(0).toUpperCase() + metric.slice(1);
            if (metric === 'duration' || metric === 'ttff') formattedValue = `${Math.round(value)}ms`;
            else if (metric === 'order') formattedValue = `${value}`;
            else formattedValue = `${Number(value).toFixed(1)}%`;
            lines.push(`${label}: ${formattedValue}`);
        });

        if (lines.length === 0) return;
        const naturalCentroid = getShapeCentroid(zone);
        const renderedPos = convertToRendered(naturalCentroid.x, naturalCentroid.y);
        const cx = renderedPos.x; const cy = renderedPos.y;

        ctx.font = "12px Arial";
        const padding = 6; const lineHeight = 18;
        let maxTextWidth = 0;
        lines.forEach(line => {
            const width = ctx.measureText(line).width;
            if (width > maxTextWidth) maxTextWidth = width;
        });

        const boxWidth = maxTextWidth + (padding * 2);
        const boxHeight = (lines.length * lineHeight) + (padding * 2);

        ctx.fillStyle = "rgba(0, 0, 0, 0.7)"; ctx.fillRect(cx - (boxWidth / 2), cy - (boxHeight / 2), boxWidth, boxHeight);
        ctx.fillStyle = "white"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        let textY = (cy - (boxHeight / 2)) + padding + (lineHeight / 2);

        lines.forEach(line => { ctx.fillText(line, cx, textY); textY += lineHeight; });
    };

    const getShapeCentroid = (zone) => {
        if (zone.shape === 'rectangle') return { x: zone.x + zone.width / 2, y: zone.y + zone.height / 2 };
        else if (zone.shape === 'circle') return { x: zone.x, y: zone.y };
        else if (zone.shape === 'polygon') {
            const sum = zone.points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
            return { x: sum.x / zone.points.length, y: sum.y / zone.points.length };
        }
        return { x: 0, y: 0 };
    };

    const getPointByIndex = (zone, index) => {
        if (zone.shape === 'polygon') return { x: zone.points[index].x, y: zone.points[index].y };
        if (zone.shape === 'rectangle') {
            switch(index) {
                case 0: return { x: zone.x, y: zone.y };
                case 1: return { x: zone.x + zone.width, y: zone.y };
                case 2: return { x: zone.x, y: zone.y + zone.height };
                case 3: return { x: zone.x + zone.width, y: zone.y + zone.height };
            }
        }
        if (zone.shape === 'circle') {
            switch(index) {
                case 0: return { x: zone.x + zone.radius, y: zone.y };
                case 1: return { x: zone.x - zone.radius, y: zone.y };
                case 2: return { x: zone.x, y: zone.y + zone.radius };
                case 3: return { x: zone.x, y: zone.y - zone.radius };
            }
        }
        return { x: 0, y: 0 };
    };

    const addZone = () => {
        if (currentShape.value && currentShape.value.shape === 'polygon' && points.value.length > 2) {
            currentShape.value.name = ""; currentShape.value.project = mediaData.value.project; currentShape.value.media = mediaData.value.id;
            currentShape.value.points = [...points.value]; 
            zones.value.push(currentShape.value);
            openEditLabelDialog(zones.value.length-1)
            points.value = []; currentShape.value = null; redrawAllZones();
        }
    }

    const undoDraw = () => {
        if ( currentShape.value && currentShape.value.shape === 'polygon') {
            points.value.pop(); redrawAllZones();
        }
    }

    const groupZones = () => {
        if (selectedZoneIndices.value.length > 1) {
            const newGroupId = generateGroupId();
            const newGroup = { temp_id: newGroupId, name: "", project: mediaData.value.project, media: mediaData.value.id, aois: [...selectedZoneIndices.value] };
            groups.value.push(newGroup);
            selectedZoneIndices.value = []; selectedZoneIndex.value = null; redrawAllZones(); openEditLabelDialog(groups.value.length-1,true)
        }
    }

    const saveZones = () => { mediaData.value.aois = zones.value; snackBarMessage.value = "Zones saved!"; isSnackbarVisible.value = true; }

    const selectGroup = (index) => {
        selectedZoneIndices.value = []
        if (selectedGroupIndex.value != index){
            selectedGroupIndex.value = index;
            groups.value[index].aois.forEach(aoi => selectZone(aoi, true))
        } else {
            selectedGroupIndex.value = null; redrawAllZones();
        }
    }

    const radiusChange =(radius) => {
        if (!heatmapInstance) return;
        const currentData = heatmapInstance.getData();
        const updatedPoints = (currentData.data || []).map(fixation => { fixation.radius = radius; return fixation; })
        heatmapInstance.setData({ ...currentData, data: updatedPoints })
        updateHeatMinMax(updatedPoints, eftImage.value.image.naturalWidth, eftImage.value.image.naturalHeight, heatmapInstance)
    }

    const exportCanvas = (type) => {
        if (!mediaData.value.media) return;
        const img = new Image(); img.crossOrigin = "anonymous"; img.src = mediaData.value.media + (mediaData.value.media.indexOf('?') > -1 ? '&t=' : '?t=') + new Date().getTime();
        img.onload = () => {
            const tempCanvas = document.createElement('canvas'); tempCanvas.width = imageNaturalWidth.value; tempCanvas.height = imageNaturalHeight.value;
            const exportCtx = tempCanvas.getContext('2d', { willReadFrequently: true }); 
            if (isGrayscale.value) exportCtx.filter = 'grayscale(100%)';
            try { exportCtx.drawImage(img, 0, 0, imageNaturalWidth.value, imageNaturalHeight.value); } catch(e) { return; }
            exportCtx.filter = 'none';
            if (type === 'heatmap' && heatmapContainer.value) {
                const hmCanvas = heatmapContainer.value.querySelector('canvas');
                if (hmCanvas) exportCtx.drawImage(hmCanvas, 0, 0, imageNaturalWidth.value, imageNaturalHeight.value);
            } else if (type === 'aoi' && drawingCanvas.value) { exportCtx.drawImage(drawingCanvas.value, 0, 0, imageNaturalWidth.value, imageNaturalHeight.value);
            } else if (type === 'scanpath' && scanpathContainer.value) {
                const svgString = new XMLSerializer().serializeToString(scanpathContainer.value); const svgImg = new Image();
                svgImg.onload = () => { exportCtx.drawImage(svgImg, 0, 0, imageNaturalWidth.value, imageNaturalHeight.value); triggerDownload(tempCanvas.toDataURL("image/png"), type); }
                svgImg.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString); return;
            }
            triggerDownload(tempCanvas.toDataURL("image/png"), type);
        };
    }
    const triggerDownload = (dataUrl, type) => {
        const link = document.createElement('a'); link.download = `${mediaData.value.name || 'export'}_${type}_${isGrayscale.value ? 'bw' : 'color'}.png`;
        link.href = dataUrl; document.body.appendChild(link); link.click(); document.body.removeChild(link);
    }

    /*** SCANPATH ***/
    const currentIndex = ref(0);
    const DURATION_SCALE = 0.3; const MIN_RADIUS = 15;
    const scanpaths = computed(() => {return (scanpathData.value?.respondents || []).map((fixation, index) => ({ ...fixation, index: index }))});
    const totalRespondents = computed(() => scanpaths.value.length);
    const currentRespondent = computed(() => { if (!scanpaths.value.length) return null; return scanpaths.value[currentIndex.value]; });
    const currentRespondentName = computed(() => currentRespondent.value?.respondent || 'Unknown' );
    const currentFixations = computed(() => currentRespondent.value?.fixations || [] );
    const saccadePoints = computed(() => { return currentFixations.value.map(f => `${f.fix_x},${f.fix_y}`).join(' '); });
    const startArrowPath = computed(() => {
        if (!currentFixations.value || currentFixations.value.length === 0) return null;
        const firstFix = currentFixations.value[0]; const secondFix = currentFixations.value[1];
        const cx = firstFix.fix_x; const cy = firstFix.fix_y;
        const dx = secondFix !== undefined ? secondFix.fix_x - cx : -cx; const dy = secondFix !== undefined ? secondFix.fix_y - cy : -cy;
        const angle = Math.atan2(dy, dx);
        const arrowLength = 50; const arrowWidth = 25; 
        const transform = (x, y) => { const rx = x * Math.cos(angle) - y * Math.sin(angle); const ry = x * Math.sin(angle) + y * Math.cos(angle); return `${cx + rx} ${cy + ry}`; };
        const startX = -arrowLength;
        return `M ${transform(startX, -arrowWidth)} L ${transform(startX + arrowLength, 0)} L ${transform(startX, arrowWidth)} L ${transform(startX + arrowLength * 0.3, 0)} Z`;
    });
    const calculateRadius = (duration) => Math.min(Math.max(duration * DURATION_SCALE, MIN_RADIUS), 60);
    const nextRespondent = () => { if (currentIndex.value < totalRespondents.value - 1) currentIndex.value++; };
    const prevRespondent = () => { if (currentIndex.value > 0) currentIndex.value--; };

    const aoisImport = async (importedAOIs) => {
        let aois = []
        let nets = importedAOIs?.nets || []
        importedAOIs.aois.map(aoi => aois.push(transformToFlatCoordinates(aoi)))
        const aoiPayload = { media: importedAOIs.media, project: importedAOIs.project, aois: aois, nets: nets }
        const newAOIs = await bulkCreateAOIs(aoiPayload)
        
        if (aois.length > 0) zones.value.push(...newAOIs.aois);
        if (nets.length > 0) groups.value.push(...newAOIs.nets);
        selectedZoneIndex.value = null; selectedZoneIndices.value = [];
        redrawAllZones();
    }

    const groupedAOIElements = computed(() => {
        const sorted = [...elementsOptions.value].sort((a, b) => a.type.localeCompare(b.type));
        const finalArray = []; let lastType = null;
        sorted.forEach((item) => {
            if (item.type !== lastType) {
                if (lastType !== null) finalArray.push({ divider: true });
                finalArray.push({ header: item.type }); lastType = item.type;
            }
            finalArray.push(item);
        });
        return finalArray;
    })

    watch(() => props.selectedItem, () => onImageLoad());
    watch([showKPIs, selectedKpiMetric], () => redrawAllZones());
    watch(showEditLabelDialog, (isOpen) => { if (!isOpen && editingZoneIndex.value !== null) cancelEditAOI(); });

    onMounted(() => {
        fetchAOIElements();
        document.addEventListener('keydown', handleKeyDown);
    })
    
    onBeforeUnmount(() => {
        document.removeEventListener('keydown', handleKeyDown);
    })

    return {
        // Refs
        drawingCanvas, eftImage, heatmapContainer, aoiDrawer, scanpathContainer,
        zoomContainerStyle, points, scrollContainer,
        
        // UI State
        zoomLevel, showHeatmap, showAOIs, isGrayscale, showKPIs, showScanpath, showImport,
        modeOptions, currentMode, heatmapOpacity, heatmapRadius,
        selectedKpiMetric, kpiMetricsOptions, clipboardAOIs,
        
        // Data
        zones, selectedZoneIndices, groups, selectedGroupIndex,
        
        // Dialogs
        showEditLabelDialog, editedZoneLabel, editedElement, isSnackbarVisible, snackBarMessage, showKPITable, showDeleteConfirmDialog, showDeleteNetConfirmDialog, 
        
        // Actions
        heatmapLoading, analysisLoading, analysisData, aoiLoading, netLoading, scanpathData, scanpathLoading, mediasLoading, elementsLoading, groupedAOIElements,
        onImageLoad, handleMouseDown, handleMouseMove, handleMouseUp,
        selectZone, openEditLabelDialog, saveEditedAOI, cancelEditAOI, deleteZone, deleteGroup, copySelectedAOIs, pasteAOIs,
        opacityChange: (v) => heatmapInstance?.configure({minOpacity: v - 0.78, maxOpacity: v}),
        radiusChange,
        
        addZone, undoDraw, groupZones, selectGroup, saveZones, exportCanvas, aoisImport,
        openDeleteConfirmDialog, cancelDeleteAoi, openDeleteNetConfirmDialog, cancelDeleteNet,

        icons: {
            mdiVectorRectangle, mdiVectorPolygon, mdiVectorCircle, mdiCursorMove, mdiPencil,
            mdiPlus, mdiContentSaveOutline, mdiUndo, mdiGroup, mdiDelete, mdiFileDownloadOutline,
            mdiFire, mdiDrawingBox, mdiInvertColors, mdiChartBar, mdiTableLarge, mdiDownload,
            mdiClose, mdiFormatListBulleted, mdiMapMarkerPath, mdiContentCopy, mdiContentPaste
        },
        
        currentIndex, DURATION_SCALE, MIN_RADIUS, scanpaths, totalRespondents,
        currentRespondentName, currentFixations, saccadePoints, startArrowPath,
        calculateRadius, nextRespondent, prevRespondent,
    }
  }
}
</script>

<style scoped>
.square-btn { min-width: 48px !important; width: 48px; height: 38px; padding: 0; }
.image-workspace-area { overflow-y: auto; padding: 0; display: flex; flex-direction: column; flex-grow: 1; }
.image-workspace-area > .v-row { flex-grow: 1; flex-wrap: nowrap; }
.image-canvas-viewport-col { flex-grow: 1; min-width: 0; padding: 0; display: flex; flex-direction: column; }
.image-canvas-scroll-container { flex-grow: 1; overflow-x: auto; overflow-y: auto; position: relative; padding: 12px; display: flex; align-items: center; }
.image-container { position: relative; display: block; background-color: #f5f5f5; }
.image-container > .v-img { width: 100%; height: 100%; position: absolute; }
#heatmap-container { z-index: 2; pointer-events: none; position: relative; overflow:hidden; }
.modes { margin-top: 10px; display: flex; gap: 10px; align-items: center; }
.controls { margin-top: 10px; display: flex; gap: 10px; align-items: center; }
#drawing-canvas.move { cursor: move; }
#drawing-canvas.edit-points { cursor: crosshair; }
#drawing-canvas { position: absolute; top: 0; left: 0; background-color: transparent; cursor: crosshair; z-index: 3; }
.layer-base { position: absolute; top: 0; left: 0; z-index: 1; }
.layer-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 2; }
.grayscale-effect { filter: grayscale(100%); transition: filter 0.3s ease; }
.scanpath-svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 20; }
.fixation-circle { transition: r 0.3s ease; }
.fixation-text { font-size: 12px; font-weight: 700; fill: white; pointer-events: none; font-family: 'Roboto', sans-serif; text-shadow: 0px 0px 3px rgba(0,0,0,0.8); }
.v-list-item.selected { background-color: #e3f2fd; }
</style>